import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InitialsAvatar from 'src/components/elements/initials-avatar/InitialsAvatar'
import { VerificationBadge } from 'src/components/elements'
import { Button } from 'src/components/ui'
import { Currency } from 'src/enums/Currency'
import { useApproveContactRevealMutation, useGetContactRevealsQuery } from 'src/redux/api'
import OfferBox from './OfferBox'
import LeaveReviewForm from './LeaveReviewForm'

interface IContactRevealsList {
  propertyId: string
  // Listing currency, so the owner can bargain per buyer in the ad's currency.
  defaultCurrency: Currency
}

// Owner-only "хто відкрив контакти" log (roadmap 41 B1). Lists everyone who
// asked for the phone; a PENDING request (auto-reveal off) gets an approve
// button. Renders nothing until someone has actually asked.
const ContactRevealsList: FC<IContactRevealsList> = ({ propertyId, defaultCurrency }) => {
  const { t } = useTranslation()
  const { data } = useGetContactRevealsQuery(propertyId)
  const [approve, { isLoading }] = useApproveContactRevealMutation()
  // Which buyer the owner is currently reviewing (one form open at a time).
  const [reviewFor, setReviewFor] = useState<string | null>(null)

  const items = data?.items ?? []
  if (!items.length) return null

  return (
    <div className="flex flex-col gap-2 w-full pt-4 border-0 border-t border-solid border-ink/[0.08]">
      <h4 className="m-0 text-sm font-extrabold leading-5 text-ink">{t('contact.whoOpened')}</h4>
      <ul className="flex flex-col gap-2 m-0 p-0 list-none">
        {items.map((reveal) => (
          <li key={reveal.id} className="flex flex-col gap-2 bg-page rounded-xl px-3 py-2.5">
            <div className="flex justify-between items-center gap-2 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <InitialsAvatar src={reveal.avatar} name={reveal.name} />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="truncate text-ink font-bold">{reveal.name}</span>
                  {reveal.isVerified && (
                    <VerificationBadge
                      className="!bg-inverse !px-1.5 !py-0.5 !rounded-full !w-auto !self-start gap-1 !text-xs"
                      verified
                    />
                  )}
                </div>
              </div>
              {reveal.status === 'PENDING' ? (
                <Button
                  type="button"
                  size="sm"
                  disabled={isLoading}
                  onClick={() => approve({ propertyId, revealId: reveal.id })}
                >
                  {t('contact.approve')}
                </Button>
              ) : (
                <span className="text-success font-bold whitespace-nowrap">
                  {t('contact.revealed')}
                </span>
              )}
            </div>
            {/* Owner bargains with this specific buyer (B2). */}
            <OfferBox
              propertyId={propertyId}
              counterpartId={reveal.userId}
              defaultCurrency={defaultCurrency}
            />
            {/* Owner rates the buyer (H1). */}
            {reviewFor === reveal.userId ? (
              <LeaveReviewForm
                propertyId={propertyId}
                revieweeId={reveal.userId}
                onDone={() => setReviewFor(null)}
              />
            ) : (
              <button
                type="button"
                className="min-h-8 text-sm font-bold text-brand-ink hover:underline self-start"
                onClick={() => setReviewFor(reveal.userId)}
              >
                {t('review.rateBuyer')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default ContactRevealsList
